import { useCallback, useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import pluralize from "pluralize";
import { match } from "ts-pattern";

import Button from "@gx/dls/buttons/Button";
import FlexWrapper, { AlignItems, JustifyContent } from "@gx/dls/containers/FlexWrapper";
import Text, { TextVariant } from "@gx/dls/text/Text";
import {
  CreateProjectRequestSchema,
  CreateProjectSourcesRequestSchema,
  UpdateProjectRequestSchema,
} from "@gx/ts-contracts/services/ontology/api/project_pb";
import { TriggerOntologyProjectSyncWorkflowRequestSchema } from "@gx/ts-contracts/services/ontology/api/temporal_pb";
import { ProjectMappingMethod } from "@gx/ts-contracts/services/project/resources/project_configuration/project_configuration_pb";

import { CreateProjectFormActionType } from "@/pages/projects/forms/create/actions";
import { CREATE_PROJECT_FORM_STEPS } from "@/pages/projects/forms/create/constants";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";
import { CreateProjectFormStep } from "@/pages/projects/forms/create/types";

import { ToastVariant } from "@/providers/toast/ToastProvider";
import { useToast } from "@/providers/toast/useToast";

import { useTriggerOntologyProjectSyncWorkflowMutation } from "@/api/mutations/ontology";
import {
  useCreateProjectMutation,
  useCreateProjectSourcesMutation,
  useUpdateProjectMutation,
} from "@/api/mutations/projects";

interface CreateProjectModalFooterProps {
  onClose: () => void;
}

const CreateProjectModalFooter = ({ onClose }: CreateProjectModalFooterProps) => {
  const navigate = useNavigate();
  const { state, dispatch } = useCreateProjectFormContext();

  const { showToast } = useToast();

  const { mutate: createProject, isPending: isCreatingProject } = useCreateProjectMutation();
  const { mutate: createProjectSources } = useCreateProjectSourcesMutation();
  const { mutate: updateProject } = useUpdateProjectMutation();
  const { mutate: triggerSyncPipeline } = useTriggerOntologyProjectSyncWorkflowMutation();

  const handleGoForward = useCallback(() => {
    dispatch({
      type: CreateProjectFormActionType.SET_STEP,
      payload: CREATE_PROJECT_FORM_STEPS[state.navigation.currentStepIndex + 1],
    });
  }, [dispatch, state.navigation.currentStepIndex]);

  const handleBuild = useCallback(() => {
    const selectedMethod = state.selectedMappingMethod;
    const sourceIds = state.createProjectSourcesRequest.sourceIds;

    // Step 1: Create the project
    createProject(
      create(CreateProjectRequestSchema, {
        name: state.createProjectRequest.name?.trim() || "",
        description: state.createProjectRequest.description?.trim() || undefined,
      }),
      {
        onSuccess: (data) => {
          const projectId = data.project?.id ?? "";

          // Step 2: Add sources to the project
          createProjectSources(
            create(CreateProjectSourcesRequestSchema, {
              projectId,
              sourceIds,
            }),
            {
              onSuccess: () => {
                // Step 3: Update project and trigger sync based on mapping method
                updateProject(
                  create(UpdateProjectRequestSchema, {
                    projectId,
                    configuration: {
                      projectMappingMethod: selectedMethod,
                      autoIntrospectPrompt: state.autoIntrospectPrompt?.trim() || undefined,
                      projectSyncCronExpression:
                        state.projectSyncCronExpression?.trim() || undefined,
                    },
                  }),
                );

                match(selectedMethod)
                  .with(ProjectMappingMethod.AUTO, () => {
                    triggerSyncPipeline(
                      create(TriggerOntologyProjectSyncWorkflowRequestSchema, {
                        input: {
                          projectId,
                          shouldRegenerate: false,
                          autoIntrospectPrompt: state.autoIntrospectPrompt,
                        },
                      }),
                      {
                        onSuccess: (syncData) => {
                          onClose();
                          void navigate({
                            to: "/projects/$projectId/graph",
                            params: { projectId },
                            search: { syncId: syncData.executionId },
                          });
                        },
                        onError: () => {
                          showToast({
                            variant: ToastVariant.ERROR,
                            header: "Failed to start auto-introspection",
                            subheader: "Something went wrong while starting the sync pipeline",
                          });
                        },
                      },
                    );
                  })
                  .with(ProjectMappingMethod.JSON, () => {
                    onClose();
                    void navigate({
                      to: "/projects/$projectId/graph",
                      params: { projectId },
                    });
                    // TODO: This should open modal for upload
                  })
                  .with(ProjectMappingMethod.BLANK, () => {
                    onClose();
                    void navigate({
                      to: "/projects/$projectId/graph",
                      params: { projectId },
                    });
                    // TODO: This should send to blank canvas
                  })
                  .otherwise(() => {
                    onClose();
                    void navigate({
                      to: "/projects/$projectId/graph",
                      params: { projectId },
                    });
                  });
              },
              onError: () => {
                showToast({
                  variant: ToastVariant.ERROR,
                  header: "Failed to add sources",
                  subheader: "Something went wrong while adding sources to the project",
                });
              },
            },
          );
        },
        onError: () => {
          showToast({
            variant: ToastVariant.ERROR,
            header: "Failed to create project",
            subheader: "Something went wrong while creating the project",
          });
        },
      },
    );
  }, [
    state.selectedMappingMethod,
    state.createProjectSourcesRequest.sourceIds,
    state.createProjectRequest,
    state.autoIntrospectPrompt,
    state.projectSyncCronExpression,
    createProject,
    createProjectSources,
    updateProject,
    triggerSyncPipeline,
    onClose,
    navigate,
    showToast,
  ]);

  const numSourcesSelected = useMemo(() => {
    return state.createProjectSourcesRequest.sourceIds?.length ?? 0;
  }, [state.createProjectSourcesRequest.sourceIds]);

  const render = useCallback(() => {
    return match(state.step)
      .returnType<React.ReactNode | null>()
      .with(CreateProjectFormStep.SELECT_PROJECT_SOURCES, () => (
        <FlexWrapper justifyContent={JustifyContent.END} gap={12} alignItems={AlignItems.CENTER}>
          {numSourcesSelected > 0 && (
            <Text variant={TextVariant.TERTIARY}>
              {numSourcesSelected} {pluralize("source", numSourcesSelected)} selected
            </Text>
          )}
          <Button
            label="Continue"
            icon={ArrowRightIcon}
            onClick={handleGoForward}
            isIconTrailing
            isDisabled={numSourcesSelected === 0}
          />
        </FlexWrapper>
      ))
      .with(CreateProjectFormStep.CONFIGURE_PROJECT, () => (
        <FlexWrapper justifyContent={JustifyContent.END} gap={12}>
          <Button
            label="Continue"
            icon={ArrowRightIcon}
            onClick={handleGoForward}
            isIconTrailing
            isDisabled={!state.createProjectRequest.name?.trim()}
          />
        </FlexWrapper>
      ))
      .with(CreateProjectFormStep.SELECT_MAPPING_METHOD, () => (
        <FlexWrapper justifyContent={JustifyContent.END} gap={12}>
          <Button
            label="Build"
            icon={ArrowRightIcon}
            onClick={handleBuild}
            isIconTrailing
            isLoading={isCreatingProject}
          />
        </FlexWrapper>
      ))
      .exhaustive();
  }, [
    state.step,
    state.createProjectRequest.name,
    numSourcesSelected,
    handleGoForward,
    handleBuild,
    isCreatingProject,
  ]);

  return render();
};

export default CreateProjectModalFooter;
